// shop-frontend/src/pages/AddProduct.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { request, toAbsUrl } from "../utils/api";
import { uploadVariantImage } from "../utils/upload";
import "../styles/addProduct.css";

/* =========================================================
   ✅ Add Product (Shop) — Option A (Industry per Product)
   - Shop picks industry per product (NOT locked to shop profile)
   - Category dropdown updates based on selected industry
   - Currency auto-set from shop country (can still change)
   - Targeting: Min Age + Max Age REQUIRED
   - Sends industryId+industryName + categoryId+categoryLabel to backend
   - Uploads images as multipart/form-data
   ========================================================= */

const COUNTRY_TO_CURRENCY = {
  "united states": "USD",
  usa: "USD",
  "u.s.a": "USD",
  "u.s.": "USD",
  us: "USD",

  canada: "CAD",

  "united kingdom": "GBP",
  uk: "GBP",
  england: "GBP",

  france: "EUR",
  germany: "EUR",
  italy: "EUR",
  spain: "EUR",
  portugal: "EUR",
  netherlands: "EUR",
  belgium: "EUR",
  austria: "EUR",
  ireland: "EUR",
  finland: "EUR",
  sweden: "SEK",
  norway: "NOK",
  denmark: "DKK",
  switzerland: "CHF",

  egypt: "EGP",
  saudi: "SAR",
  "saudi arabia": "SAR",
  uae: "AED",
  "united arab emirates": "AED",
  qatar: "QAR",
  kuwait: "KWD",
  jordan: "JOD",
  lebanon: "LBP",

  india: "INR",
  pakistan: "PKR",
  bangladesh: "BDT",
  china: "CNY",
  japan: "JPY",
  korea: "KRW",
  "south korea": "KRW",
  vietnam: "VND",
  turkey: "TRY",
  brazil: "BRL",
  mexico: "MXN",
  nigeria: "NGN",
  kenya: "KES",
  australia: "AUD",
};

const OPTION_TYPE_OPTIONS = ["Color", "Size", "Material", "Style"];
const COMMON_COLOR_NAMES = new Set([
  "red",
  "green",
  "blue",
  "pink",
  "black",
  "white",
  "yellow",
  "orange",
  "purple",
  "brown",
  "gray",
  "grey",
]);
const COMMON_SIZE_VALUES = new Set(["xxs", "xs", "s", "m", "l", "xl", "xxl", "xxxl"]);

function normStr(v) {
  return String(v || "").trim();
}
function normLower(v) {
  return normStr(v).toLowerCase();
}
function normLowerList(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((x) => String(x || "").toLowerCase().trim())
    .filter(Boolean);
}

function splitOptionValues(input) {
  return String(input || "")
    .split(/[\n,،;]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function isLikelySizeValue(v) {
  const value = normLower(v);
  if (!value) return false;
  if (COMMON_SIZE_VALUES.has(value)) return true;
  if (/^\d{1,3}$/.test(value)) return true;
  if (/^\d{1,3}(cm|mm|in)$/.test(value)) return true;
  return false;
}

function cartesianOptionRows(options) {
  const clean = (Array.isArray(options) ? options : [])
    .map((opt) => ({
      name: normStr(opt?.name),
      values: Array.isArray(opt?.values) ? opt.values.map((v) => normStr(v)).filter(Boolean) : [],
    }))
    .filter((opt) => opt.name && opt.values.length > 0);

  if (clean.length === 0) return [];

  let rows = [[]];
  for (const opt of clean) {
    const next = [];
    for (const row of rows) {
      for (const v of opt.values) {
        next.push([...row, { name: opt.name, value: v }]);
      }
    }
    rows = next;
  }
  return rows;
}

function normIndustries(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.industries)) return payload.industries;
  if (payload.data && Array.isArray(payload.data.industries)) return payload.data.industries;
  return [];
}

// supports multiple shapes from your backend
function getIndustryId(ind) {
  return normLower(ind?.industryId || ind?._id || ind?.id || "");
}
function getIndustryName(ind) {
  return normStr(ind?.name || ind?.label || "");
}
function getCategoryId(cat) {
  return normLower(cat?.categoryId || cat?._id || cat?.id || "");
}
function getCategoryLabel(cat) {
  return normStr(cat?.label || cat?.name || "");
}

function safeCurrencyFromCountry(country) {
  const c = normLower(country);
  if (!c) return "USD";
  return COUNTRY_TO_CURRENCY[c] || "USD";
}

export default function AddProduct() {
  const nav = useNavigate();
  const prevCurrencyWasUserEdited = useRef(false);
  const optionNameRef = useRef(null);

  // ===== Product fields =====
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // ✅ Option A: industry per product
  const [industryId, setIndustryId] = useState("");
  const [industryName, setIndustryName] = useState("");

  const [categoryId, setCategoryId] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");

  const [localPrice, setLocalPrice] = useState("");
  const [currency, setCurrency] = useState("USD");

  const [inStock, setInStock] = useState(true);
  const [quantity, setQuantity] = useState(1);

  // ===== Shipping Profile =====
  const [shippingIsDigital, setShippingIsDigital] = useState(false);
  const [shippingWeightOz, setShippingWeightOz] = useState("");
  const [shippingLengthIn, setShippingLengthIn] = useState("");
  const [shippingWidthIn, setShippingWidthIn] = useState("");
  const [shippingHeightIn, setShippingHeightIn] = useState("");
  const [shippingPackageCode, setShippingPackageCode] = useState("package");
  const [weightUnit, setWeightUnit] = useState("oz"); // oz | g | kg | lb
  const [dimUnit, setDimUnit] = useState("in");       // in | cm

  // Convert user-entered value to oz (for weight) or in (for dimensions)
  function toOz(val, unit) {
    const n = Number(val);
    if (!Number.isFinite(n)) return n;
    if (unit === "g")  return n / 28.3495;
    if (unit === "kg") return n * 35.274;
    if (unit === "lb") return n * 16;
    return n; // oz
  }
  function toIn(val, unit) {
    const n = Number(val);
    if (!Number.isFinite(n)) return n;
    if (unit === "cm") return n / 2.54;
    return n; // in
  }
  const [showShipFromOverride, setShowShipFromOverride] = useState(false);
  const [shipFromOverride, setShipFromOverride] = useState({
    name: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    phone: "",
  });

  // ===== Variants =====
  const [optionTypeInput, setOptionTypeInput] = useState("");
  const [optionNameInput, setOptionNameInput] = useState("");
  const [optionValuesInput, setOptionValuesInput] = useState("");
  const [options, setOptions] = useState([]);
  const [variants, setVariants] = useState([]);
  const [variantWarning, setVariantWarning] = useState("");
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkStock, setBulkStock] = useState("");
  const [uploadingColorKey, setUploadingColorKey] = useState("");
  const [colorStockDraft, setColorStockDraft] = useState({});
  const [variantQuery, setVariantQuery] = useState("");

  // ===== Images =====
  const [images, setImages] = useState([]); // File[]

  // ===== Targeting =====
  const [genderMode, setGenderMode] = useState("any"); // any|male|female
  const [minAge, setMinAge] = useState(""); // REQUIRED
  const [maxAge, setMaxAge] = useState(""); // REQUIRED

  const [sports, setSports] = useState([]);
  const [interests, setInterests] = useState([]);

  // ===== Data =====
  const [shopCountry, setShopCountry] = useState("");

  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);

  // ===== UI state =====
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // ===== Image previews =====
  const previews = useMemo(() => {
    return images.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }));
  }, [images]);

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  // ✅ Load shop info (country) + load industries list (public)
  useEffect(() => {
    let ignore = false;

    async function load() {
      setErr("");
      setLoadingIndustries(true);

      try {
        // shop info from localStorage (country only used for currency)
        const shopStr = localStorage.getItem("shop");
        const shop = shopStr ? JSON.parse(shopStr) : null;

        const country = normStr(shop?.country || "");
        const autoCurrency = safeCurrencyFromCountry(country);

        if (!ignore) {
          setShopCountry(country);
          if (!prevCurrencyWasUserEdited.current) setCurrency(autoCurrency);
        }

        // industries list
        const data = await request("/api/public/industries", { method: "GET" });
        const list = normIndustries(data);

        const normalized = (Array.isArray(list) ? list : [])
          .map((i) => ({
            id: getIndustryId(i),
            name: getIndustryName(i),
          }))
          .filter((i) => i.id && i.name)
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!ignore) {
          setIndustries(normalized);

          // auto-select first industry (only if none picked yet)
          if (!industryId && normalized.length === 1) {
            setIndustryId(normalized[0].id);
            setIndustryName(normalized[0].name);
          }
        }
      } catch (e) {
        if (!ignore) {
          setErr(
            e?.response?.data?.message ||
              e?.message ||
              "Failed to load industries. Make sure GET /api/public/industries works."
          );
          setIndustries([]);
        }
      } finally {
        if (!ignore) setLoadingIndustries(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ When industry changes, load categories for that industry
  useEffect(() => {
    let ignore = false;

    async function loadCats() {
      setCategories([]);
      setCategoryId("");
      setCategoryLabel("");
      if (!industryId) return;

      setLoadingCats(true);
      setErr("");

      try {
        const data = await request(`/api/public/industries/${industryId}/categories`, { method: "GET" });

        const catsRaw = Array.isArray(data?.categories) ? data.categories : [];
        const normalized = catsRaw
          .map((c) => ({
            id: getCategoryId(c),
            label: getCategoryLabel(c),
          }))
          .filter((c) => c.id && c.label)
          .sort((a, b) => a.label.localeCompare(b.label));

        if (!ignore) {
          setCategories(normalized);

          // if backend returns industryName, keep it
          const backendIndustryName = normStr(data?.industryName || "");
          if (backendIndustryName) setIndustryName(backendIndustryName);

          // auto-select if only one category
          if (normalized.length === 1) {
            setCategoryId(normalized[0].id);
            setCategoryLabel(normalized[0].label);
          }
        }
      } catch (e) {
        if (!ignore) {
          setErr(
            e?.response?.data?.message ||
              e?.message ||
              "Failed to load categories. Make sure GET /api/public/industries/:industryId/categories works."
          );
          setCategories([]);
        }
      } finally {
        if (!ignore) setLoadingCats(false);
      }
    }

    loadCats();
    return () => {
      ignore = true;
    };
  }, [industryId]);

  function onPickImages(e) {
    const picked = Array.from(e.target.files || []);
    setImages(picked.slice(0, 6));
  }
  function removeImage(idx) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }

  function onPickIndustry(id) {
    const chosen = normLower(id);
    setIndustryId(chosen);

    const found = industries.find((x) => x.id === chosen) || null;
    setIndustryName(found?.name || "");
  }

  function onPickCategory(id) {
    const chosen = normLower(id);
    setCategoryId(chosen);

    const found = categories.find((c) => c.id === chosen) || null;
    setCategoryLabel(found?.label || "");
  }

  function addOption() {
    const selectedType = OPTION_TYPE_OPTIONS.find((opt) => normLower(opt) === normLower(optionTypeInput)) || "";
    const customName = normStr(optionNameInput);
    const safeCustomName =
      COMMON_COLOR_NAMES.has(normLower(customName)) && selectedType
        ? selectedType
        : customName;
    const name = safeCustomName || selectedType;
    const lowName = normLower(name);
    const values = splitOptionValues(optionValuesInput);

    if (!selectedType) {
      setErr("Option type is required.");
      return;
    }
    if (!name) {
      setErr("Option name is required.");
      return;
    }
    if (COMMON_COLOR_NAMES.has(lowName)) {
      setErr("Option name should be Color/Size/Material/Style. Put red/blue/green in Values.");
      return;
    }
    if (values.length === 0) {
      setErr("Option values are required (comma-separated).");
      return;
    }

    const isColorType = normLower(selectedType) === "color";
    const typedColorName = COMMON_COLOR_NAMES.has(normLower(customName)) ? customName : "";
    const allValuesLookLikeSize = values.length > 0 && values.every((v) => isLikelySizeValue(v));

    if (isColorType && typedColorName && allValuesLookLikeSize) {
      setErr("");
      setOptions((prev) => {
        const upsert = (list, optionName, vals) => {
          const idx = list.findIndex((o) => normLower(o.name) === normLower(optionName));
          if (idx === -1) return [...list, { name: optionName, values: vals }];
          const merged = [...new Set([...(Array.isArray(list[idx].values) ? list[idx].values : []), ...vals])];
          return list.map((opt, i) => (i === idx ? { ...opt, values: merged } : opt));
        };

        let next = upsert(prev, "Color", [typedColorName]);
        next = upsert(next, "Size", values);
        return next;
      });

      setOptionTypeInput("");
      setOptionNameInput("");
      setOptionValuesInput("");
      requestAnimationFrame(() => optionNameRef.current?.focus());
      return;
    }

    const dup = options.some((o) => normLower(o.name) === normLower(name));
    if (dup) {
      setErr(`Option "${name}" already exists.`);
      return;
    }

    setErr("");
    setOptions((prev) => [...prev, { name, values }]);
    setOptionTypeInput("");
    setOptionNameInput("");
    setOptionValuesInput("");
    requestAnimationFrame(() => optionNameRef.current?.focus());
  }

  function onOptionInputKeyDown(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    addOption();
  }

  function removeOption(name) {
    const key = normLower(name);
    setOptions((prev) => prev.filter((o) => normLower(o.name) !== key));
    setVariants([]);
    setVariantWarning("");
  }

  function variantKey(optionValues) {
    return (Array.isArray(optionValues) ? optionValues : [])
      .map((ov) => `${normLower(ov?.name)}:${normLower(ov?.value)}`)
      .sort()
      .join("|");
  }

  function generateVariants() {
    const badOption = options.find((o) => COMMON_COLOR_NAMES.has(normLower(o?.name)));
    if (badOption) {
      setErr("Option name should be ‘Color’. Put red/blue/green in Values.");
      return;
    }

    if (!options.length || options.some((o) => !Array.isArray(o.values) || o.values.length === 0)) {
      setErr("Please add at least one option with values before generating variants.");
      return;
    }

    const rows = cartesianOptionRows(options);
    if (rows.length === 0) {
      setErr("Add at least one option with values before generating variants.");
      return;
    }

    const byKey = new Map(
      variants.map((v) => [variantKey(v.optionValues), v])
    );

    const generated = rows.map((row) => {
      const key = variantKey(row);
      const existing = byKey.get(key);
      return {
        optionValues: row,
        sku: existing?.sku || "",
        priceOverride: existing?.priceOverride ?? "",
        stock: existing?.stock ?? 0,
        images: Array.isArray(existing?.images) ? existing.images : [],
        isActive: existing?.isActive !== false,
      };
    });

    setVariants(generated);
    setVariantWarning("");
    setErr("");
  }

  const optionValueOf = useCallback((variant, optionName) => {
    const target = normLower(optionName);
    const found = (Array.isArray(variant?.optionValues) ? variant.optionValues : []).find(
      (ov) => normLower(ov?.name) === target
    );
    return normStr(found?.value);
  }, []);

  const isVariantMatch = useCallback((variant, colorName, colorValue, sizeName, sizeValue) => {
    const c = optionValueOf(variant, colorName);
    const s = sizeName ? optionValueOf(variant, sizeName) : "";
    if (normLower(c) !== normLower(colorValue)) return false;
    if (sizeName) return normLower(s) === normLower(sizeValue);
    return true;
  }, [optionValueOf]);

  function updateVariantByColorAndSize(colorName, colorValue, sizeName, sizeValue, patch) {
    setVariants((prev) =>
      prev.map((v) =>
        isVariantMatch(v, colorName, colorValue, sizeName, sizeValue) ? { ...v, ...patch } : v
      )
    );
  }

  function applyBulkPrice() {
      const p = Number(bulkPrice);
      if (!Number.isFinite(p) || p < 0) {
      setErr("Bulk price must be a number >= 0.");
      return;
    }
      setErr("");
      setVariants((prev) => prev.map((v) => ({ ...v, priceOverride: p })));
  }

  function applyBulkStock() {
      const s = Number(bulkStock);
      if (!Number.isFinite(s) || s < 0) {
      setErr("Bulk stock must be a number >= 0.");
      return;
    }
      setErr("");
      setVariants((prev) => prev.map((v) => ({ ...v, stock: Math.floor(s) })));
  }

  async function handleUploadForColor(colorName, colorValue, e) {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const colorKey = `${normLower(colorName)}:${normLower(colorValue)}`;
    const currentGroupImages = [
      ...new Set(
        variants
          .filter((v) => normLower(optionValueOf(v, colorName)) === normLower(colorValue))
          .flatMap((v) => (Array.isArray(v.images) ? v.images : []))
          .filter(Boolean)
      ),
    ];
    if (currentGroupImages.length >= 6) {
      setErr("You can upload up to 6 images per option box.");
      if (e?.target) e.target.value = "";
      return;
    }

    setUploadingColorKey(colorKey);
    setErr("");
    try {
      const url = await uploadVariantImage(file);
      setVariants((prev) =>
        prev.map((v) => {
          if (normLower(optionValueOf(v, colorName)) !== normLower(colorValue)) return v;
          const existing = Array.isArray(v.images) ? v.images : [];
          const next = existing.includes(url) ? existing : [...existing, url].slice(0, 6);
          return { ...v, images: next };
        })
      );
    } catch (uploadErr) {
      setErr(uploadErr?.message || "Variant image upload failed.");
    } finally {
      setUploadingColorKey("");
      if (e?.target) e.target.value = "";
    }
  }

  function removeColorImage(colorName, colorValue, url) {
    setVariants((prev) =>
      prev.map((v) => {
        if (normLower(optionValueOf(v, colorName)) !== normLower(colorValue)) return v;
        return {
          ...v,
          images: (Array.isArray(v.images) ? v.images : []).filter((u) => u !== url),
        };
      })
    );
  }

  function applyStockToColor(colorName, colorValue) {
    const key = normLower(colorValue);
    const raw = colorStockDraft[key];
    const stock = Number(raw);
    if (!Number.isFinite(stock) || stock < 0) {
      setErr("Box stock must be a number >= 0.");
      return;
    }
    setErr("");
    setVariants((prev) =>
      prev.map((v) => {
        if (normLower(optionValueOf(v, colorName)) !== key) return v;
        return { ...v, stock: Math.floor(stock) };
      })
    );
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    const cleanTitle = normStr(title);
    if (!cleanTitle) return setErr("Title is required.");
    if (cleanTitle.length < 10) return setErr("Product title must be at least 10 characters long for better discoverability.");
    if (cleanTitle.length > 80) return setErr("Product title must be 80 characters or less.");
    
    // Check for common typos and poor quality titles
    const titleLower = cleanTitle.toLowerCase();
    const poorQualityPatterns = [
      /\bshose\b/,  // common typo for "shoes"
      /^.{1,5}$/,   // very short titles
      /^\w+$/, // single word titles
      /product|item|thing/i, // generic words
    ];
    
    if (poorQualityPatterns.some(pattern => pattern.test(titleLower))) {
      return setErr("Please use a descriptive, well-spelled product title (e.g., 'Nike Air Max Running Shoes' instead of 'shose' or 'product').");
    }
    
    // Validate description
    const cleanDescription = normStr(description);
    if (!cleanDescription) return setErr("Product description is required to help customers understand what you're selling.");
    if (cleanDescription.length < 20) return setErr("Product description must be at least 20 characters long.");
    if (cleanDescription.length > 2000) return setErr("Product description must be 2000 characters or less.");

    if (!industryId) return setErr("Industry is required. Please choose an industry.");
    if (!categoryId) return setErr("Category is required. Please choose from the dropdown.");

    // REQUIRED targeting age
    const minAgeNum = Number(minAge);
    const maxAgeNum = Number(maxAge);

    if (!Number.isFinite(minAgeNum) || minAgeNum < 0) {
      return setErr("Min Age is required and must be a valid number ≥ 0.");
    }
    if (!Number.isFinite(maxAgeNum) || maxAgeNum < 0) {
      return setErr("Max Age is required and must be a valid number ≥ 0.");
    }
    if (minAgeNum > maxAgeNum) {
      return setErr("Min Age cannot be greater than Max Age.");
    }

    const weightOzNum = toOz(shippingWeightOz, weightUnit);
    const lengthInNum = toIn(shippingLengthIn, dimUnit);
    const widthInNum = toIn(shippingWidthIn, dimUnit);
    const heightInNum = toIn(shippingHeightIn, dimUnit);

    if (!shippingIsDigital) {
      if (
        !Number.isFinite(weightOzNum) ||
        weightOzNum < 0.1 ||
        !Number.isFinite(lengthInNum) ||
        lengthInNum < 1 ||
        !Number.isFinite(widthInNum) ||
        widthInNum < 1 ||
        !Number.isFinite(heightInNum) ||
        heightInNum < 1
      ) {
        return setErr("Shipping profile is required: weightOz + lengthIn/widthIn/heightIn");
      }
    }

    // variants validation
    const hasVariantConfig = options.length > 0;
    const hasVariants = variants.length > 0;
    if (hasVariantConfig && !hasVariants) {
      return setErr("Generate variants before submitting.");
    }

    const normalizedOptions = options.map((opt) => ({
      name: normStr(opt.name),
      values: splitOptionValues(Array.isArray(opt.values) ? opt.values.join(",") : opt.values),
    }));

    const normalizedVariants = variants.map((v) => {
      const stockNum = Number(v.stock);
      const priceNum =
        v.priceOverride === "" || v.priceOverride === null || typeof v.priceOverride === "undefined"
          ? null
          : Number(v.priceOverride);
      return {
        optionValues: Array.isArray(v.optionValues) ? v.optionValues : [],
        sku: normStr(v.sku),
        price: Number.isFinite(priceNum) ? priceNum : null,
        stock: Number.isFinite(stockNum) ? Math.max(0, Math.floor(stockNum)) : NaN,
        images: Array.isArray(v.images) ? v.images.map((x) => normStr(x)).filter(Boolean) : [],
      };
    });

    for (const row of normalizedVariants) {
      if (!Number.isFinite(row.stock) || row.stock < 0) {
        return setErr("Each variant stock must be a valid number ≥ 0.");
      }
      if (row.price !== null && (!Number.isFinite(row.price) || row.price < 0)) {
        return setErr("Each variant price must be empty or a number ≥ 0.");
      }
    }

    const rawBasePrice = Number(localPrice);
    const variantPriceCandidates = normalizedVariants
      .map((v) => Number(v.price))
      .filter((p) => Number.isFinite(p) && p > 0);

    const priceNum =
      Number.isFinite(rawBasePrice) && rawBasePrice > 0
        ? rawBasePrice
        : variantPriceCandidates.length > 0
        ? Math.min(...variantPriceCandidates)
        : NaN;

    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      return setErr("Add Price > 0, or set at least one variant price > 0.");
    }

    if (!(Number.isFinite(rawBasePrice) && rawBasePrice > 0)) {
      setLocalPrice(String(priceNum));
    }

    const colorOption = normalizedOptions.find((o) => normLower(o.name) === "color");
    if (colorOption && normalizedVariants.length > 0) {
      const colorsWithoutImages = colorOption.values.filter((color) => {
        const rowsForColor = normalizedVariants.filter((v) =>
          v.optionValues.some((ov) => normLower(ov.name) === "color" && normLower(ov.value) === normLower(color))
        );
        return !rowsForColor.some((r) => Array.isArray(r.images) && r.images.length > 0);
      });
      if (colorsWithoutImages.length > 0) {
        setVariantWarning(`Recommendation: add at least one image for colors: ${colorsWithoutImages.join(", ")}`);
      } else {
        setVariantWarning("");
      }
    } else {
      setVariantWarning("");
    }

    // backend: genders[] (empty => show to all)
    const genders = genderMode === "any" ? [] : [normLower(genderMode)];

    // multipart FormData
    const fd = new FormData();
    fd.append("title", cleanTitle);
    fd.append("description", normStr(description));

    // ✅ Option A fields
    fd.append("industryId", normLower(industryId));
    fd.append("industryName", normStr(industryName)); // backend will also fill if missing
    fd.append("categoryId", normLower(categoryId));
    fd.append("categoryLabel", normStr(categoryLabel));

    fd.append("localPrice", String(priceNum));
    fd.append("basePrice", String(priceNum));

    // backend will force currency anyway; keeping it is harmless
    fd.append("currency", String(currency || safeCurrencyFromCountry(shopCountry) || "USD").toUpperCase());

    fd.append("inStock", String(!!inStock));
    fd.append("quantity", String(Math.max(0, Math.floor(Number(quantity) || 0))));

    fd.append(
      "targeting",
      JSON.stringify({
        genders,
        minAge: minAgeNum,
        maxAge: maxAgeNum,
        interests: normLowerList(interests),
        sports: normLowerList(sports),
      })
    );

    fd.append("options", JSON.stringify(normalizedOptions));
    fd.append("variants", JSON.stringify(normalizedVariants));

    fd.append(
      "shippingProfile",
      JSON.stringify({
        isDigital: Boolean(shippingIsDigital),
        weightOz: shippingIsDigital ? null : weightOzNum,
        lengthIn: shippingIsDigital ? null : lengthInNum,
        widthIn: shippingIsDigital ? null : widthInNum,
        heightIn: shippingIsDigital ? null : heightInNum,
        packageCode: normStr(shippingPackageCode || "package") || "package",
        shipFromOverride: showShipFromOverride
          ? {
              name: normStr(shipFromOverride.name),
              street1: normStr(shipFromOverride.street1),
              street2: normStr(shipFromOverride.street2),
              city: normStr(shipFromOverride.city),
              state: normStr(shipFromOverride.state),
              postalCode: normStr(shipFromOverride.postalCode),
              country: normStr(shipFromOverride.country),
              phone: normStr(shipFromOverride.phone),
            }
          : undefined,
      })
    );

    for (const f of images) {
      fd.append("images", f);
    }

    setSaving(true);
    try {
      const result = await request("/api/shop/products", {
        method: "POST",
        body: fd,
      });

      if (result?.warning) {
        // Saved as draft — show warning then navigate
        alert(`⚠️ ${result.warning}`);
      }
      nav("/shop/products");
    } catch (e2) {
      console.error("AddProduct error:", e2);
      const msg =
        e2?.data?.error || e2?.data?.message || e2?.response?.data?.message || e2?.message || "Failed to create product";
      setErr(msg);
    } finally {
      setSaving(false);
    }
  }

  const industryOptions = useMemo(() => industries, [industries]);
  const categoryOptions = useMemo(() => categories, [categories]);
  const colorOption = useMemo(
    () => options.find((o) => normLower(o?.name) === "color") || null,
    [options]
  );
  const sizeOption = useMemo(
    () => options.find((o) => normLower(o?.name) === "size") || null,
    [options]
  );

  const colorValues = useMemo(() => {
    if (colorOption?.values?.length) return colorOption.values;
    const fromVariants = variants.map((v) => optionValueOf(v, "color")).filter(Boolean);
    return [...new Set(fromVariants)];
  }, [colorOption, variants, optionValueOf]);

  const sizeValues = useMemo(() => {
    if (sizeOption?.values?.length) return sizeOption.values;
    const fromVariants = variants.map((v) => optionValueOf(v, "size")).filter(Boolean);
    return [...new Set(fromVariants)];
  }, [sizeOption, variants, optionValueOf]);

  const colorGroups = useMemo(() => {
    if (!colorOption) return [];

    const q = normLower(variantQuery);
    const groups = colorValues
      .map((color) => {
        const rows = (sizeOption ? sizeValues : [""]).map((size) => {
          const variant = variants.find((v) =>
            isVariantMatch(v, colorOption.name, color, sizeOption?.name || "", size)
          );
          return {
            size,
            variant,
          };
        });

        const images = [
          ...new Set(
            rows
              .flatMap((r) => (Array.isArray(r.variant?.images) ? r.variant.images : []))
              .filter(Boolean)
          ),
        ];

        const matchesFilter =
          !q ||
          normLower(color).includes(q) ||
          rows.some((r) =>
            normLower(r.size).includes(q) || normLower(r.variant?.sku).includes(q)
          );

        return {
          color,
          rows,
          images,
          matchesFilter,
        };
      })
      .filter((g) => g.matchesFilter);

    return groups;
  }, [colorOption, sizeOption, colorValues, sizeValues, variants, variantQuery, isVariantMatch]);

  const variantRows = useMemo(() => {
    const q = normLower(variantQuery);
    const rows = variants.map((variant, index) => {
      const optionText = (Array.isArray(variant?.optionValues) ? variant.optionValues : [])
        .map((ov) => `${normStr(ov?.name)}: ${normStr(ov?.value)}`)
        .filter(Boolean)
        .join(" / ");
      return { index, variant, optionText };
    });

    if (!q) return rows;
    return rows.filter((row) =>
      normLower(row.optionText).includes(q) || normLower(row.variant?.sku).includes(q)
    );
  }, [variants, variantQuery]);

  return (
    <div style={S.wrap} className="ap-form">
      <style>{`
        select { color-scheme: inherit; }
      `}</style>

      <div className="ap-save-bar">
        <h1>Add Product</h1>
        <div className="ap-save-actions">
          <button style={S.btnGhost} type="button" onClick={() => nav("/shop/products")}>← Back</button>
          <button style={S.btnPrimary} type="button" onClick={(e) => { const form = e.target.closest(".ap-form")?.querySelector("form"); if (form) form.requestSubmit(); }} disabled={saving}>
            {saving ? "Saving..." : "Save Product"}
          </button>
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ display: "grid", gap: 16 }}>
        {!!err && <div style={S.errBox}>{err}</div>}

        {/* Product Info Card */}
        <div style={S.card}>
          <div style={S.sectionTitle}>📦 Product Info</div>

          <div style={S.row2}>
            <Field label="Product Title *">
              <input 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                style={S.input} 
                disabled={saving}
                placeholder="e.g., Nike Air Max Running Shoes - Men's Size 10"
                minLength={10}
                maxLength={80}
              />
              <div style={S.help}>
                Be descriptive and specific (10-80 characters). Include brand, product type, and key details.
                Good: "Wireless Bluetooth Headphones - Noise Cancelling" | 
                Avoid: "headphones" or "product"
              </div>
            </Field>
            
            <Field label="Product Description *">
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                style={{...S.input, minHeight: '120px', resize: 'vertical'}} 
                disabled={saving}
                placeholder="Describe your product in detail. Include key features, materials, dimensions, care instructions, and what makes it special. Help customers understand exactly what they're buying."
                minLength={20}
                maxLength={2000}
                rows={5}
              />
              <div style={S.help}>
                Provide detailed information (20-2000 characters). Include features, materials, size, care instructions, and benefits.
                This helps customers make informed decisions and reduces returns.
                <span style={{float: 'right', fontSize: '12px', color: '#888'}}>
                  {description.length}/2000 characters
                </span>
              </div>
            </Field>

            <Field label="Industry *">
              <select
                value={industryId}
                onChange={(e) => onPickIndustry(e.target.value)}
                style={S.select}
                disabled={saving || loadingIndustries}
                required
              >
                <option value="">{loadingIndustries ? "Loading industries..." : "Select industry"}</option>
                {industryOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>

              {!loadingIndustries && industryOptions.length === 0 ? (
                <div style={S.help}>No industries found. Check /api/public/industries.</div>
              ) : null}
            </Field>
          </div>

          <div style={S.row2}>
            <Field label="Category * (based on selected industry)">
              <select
                value={categoryId}
                onChange={(e) => onPickCategory(e.target.value)}
                style={S.select}
                disabled={saving || loadingCats || !industryId}
                required
              >
                <option value="">
                  {!industryId
                    ? "Select industry first"
                    : loadingCats
                    ? "Loading categories..."
                    : "Select category"}
                </option>
                {categoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>

              {!loadingCats && industryId && categoryOptions.length === 0 ? (
                <div style={S.help}>No categories found for this industry.</div>
              ) : null}
            </Field>

            <Field label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={S.textarea}
                disabled={saving}
              />
            </Field>
          </div>
        </div>

        {/* Images Card — placed first so it's never missed */}
        <div style={{ ...S.card, border: images.length === 0 ? "2px dashed #f59e0b" : "2px solid #22c55e" }}>
          <div style={S.sectionTitle}>
            📷 Product Images {images.length === 0 && <span style={{ fontSize: 13, color: "#f59e0b", fontWeight: 700, marginLeft: 8 }}>⚠️ No images yet — add at least one!</span>}
          </div>
          <div style={S.help}>Main photos shown in the mall, product detail page and search. Up to 6 photos.</div>

          <div style={S.uploadBox}>
            <label style={S.uploadBtn}>
              📷 Choose Images (up to 6)
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={onPickImages}
                style={{ display: "none" }}
                disabled={saving}
              />
            </label>

            {images.length > 0 ? (
              <div style={S.previewGrid}>
                {previews.map((p, idx) => (
                  <div key={idx} style={S.previewCard}>
                    <img src={p.url} alt="" style={S.previewImg} />
                    <div style={S.previewName}>{p.name}</div>
                    <button type="button" onClick={() => removeImage(idx)} style={S.removeBtn} disabled={saving}>✕</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#f59e0b", fontWeight: 600 }}>No images selected — products without photos get much less visibility in the mall.</div>
            )}
          </div>
        </div>

        {/* Pricing Card */}
        <div style={S.card}>
          <div style={S.sectionTitle}>💰 Pricing & Stock</div>

          <div style={S.row3}>
            <Field label="Price *">
              <input
                value={localPrice}
                onChange={(e) => setLocalPrice(e.target.value)}
                style={S.input}
                inputMode="decimal"
                disabled={saving}
              />
            </Field>

            <Field label="Currency (auto from country)">
              <select
                value={currency}
                onChange={() => {}}
                style={S.select}
                disabled
              >
                <option value={currency}>{currency || "USD"}</option>
              </select>
              <div style={S.help}>
                Default currency from country: <b>{safeCurrencyFromCountry(shopCountry)}</b>
              </div>
            </Field>

            <Field label="Quantity">
              <input
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={S.input}
                inputMode="numeric"
                disabled={saving}
              />
            </Field>
          </div>

          <div style={S.switchRow}>
            <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} disabled={saving} />
            <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>In Stock</span>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.sectionTitle}>🚚 Shipping Profile</div>
          <div style={S.help}>Accurate shipping prices require real weight and dimensions.</div>
          <div style={S.help}>Missing shipping info will use defaults and may show incorrect rates.</div>

          <div style={{ ...S.switchRow, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={shippingIsDigital}
              onChange={(e) => setShippingIsDigital(e.target.checked)}
              disabled={saving}
            />
            <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>Digital product (no shipping)</span>
          </div>

          <div style={S.row3}>
            <Field label={`Weight ${shippingIsDigital ? "" : "*"}`}>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={shippingWeightOz}
                  onChange={(e) => setShippingWeightOz(e.target.value)}
                  style={{ ...S.input, flex: 1 }}
                  inputMode="decimal"
                  disabled={saving || shippingIsDigital}
                  placeholder="e.g. 12"
                />
                <select
                  value={weightUnit}
                  onChange={(e) => setWeightUnit(e.target.value)}
                  style={{ ...S.select, width: 70 }}
                  disabled={saving || shippingIsDigital}
                >
                  <option value="oz">oz</option>
                  <option value="g">g</option>
                  <option value="kg">kg</option>
                  <option value="lb">lb</option>
                </select>
              </div>
            </Field>

            <Field label={`Length ${shippingIsDigital ? "" : "*"}`}>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={shippingLengthIn}
                  onChange={(e) => setShippingLengthIn(e.target.value)}
                  style={{ ...S.input, flex: 1 }}
                  inputMode="decimal"
                  disabled={saving || shippingIsDigital}
                  placeholder="e.g. 10"
                />
                <select
                  value={dimUnit}
                  onChange={(e) => setDimUnit(e.target.value)}
                  style={{ ...S.select, width: 70 }}
                  disabled={saving || shippingIsDigital}
                >
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                </select>
              </div>
            </Field>

            <Field label={`Width ${shippingIsDigital ? "" : "*"}`}>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={shippingWidthIn}
                  onChange={(e) => setShippingWidthIn(e.target.value)}
                  style={{ ...S.input, flex: 1 }}
                  inputMode="decimal"
                  disabled={saving || shippingIsDigital}
                  placeholder="e.g. 8"
                />
                <span style={{ lineHeight: "38px", fontSize: 12, color: "var(--text-secondary)", flexShrink: 0 }}>{dimUnit}</span>
              </div>
            </Field>
          </div>

          <div style={S.row3}>
            <Field label={`Height ${shippingIsDigital ? "" : "*"}`}>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  value={shippingHeightIn}
                  onChange={(e) => setShippingHeightIn(e.target.value)}
                  style={{ ...S.input, flex: 1 }}
                  inputMode="decimal"
                  disabled={saving || shippingIsDigital}
                  placeholder="e.g. 4"
                />
                <span style={{ lineHeight: "38px", fontSize: 12, color: "var(--text-secondary)", flexShrink: 0 }}>{dimUnit}</span>
              </div>
            </Field>

            <Field label="Package code">
              <select
                value={shippingPackageCode}
                onChange={(e) => setShippingPackageCode(e.target.value)}
                style={S.select}
                disabled={saving}
              >
                <option value="package">package</option>
                <option value="small_flat_rate_box">small_flat_rate_box</option>
                <option value="medium_flat_rate_box">medium_flat_rate_box</option>
                <option value="large_flat_rate_box">large_flat_rate_box</option>
              </select>
            </Field>

            <Field label="Ship-from override (optional)">
              <button
                type="button"
                style={S.btnGhost}
                onClick={() => setShowShipFromOverride((v) => !v)}
                disabled={saving}
              >
                {showShipFromOverride ? "Hide override" : "Add override"}
              </button>
            </Field>
          </div>

          {showShipFromOverride ? (
            <div style={S.row2}>
              <Field label="Override name">
                <input
                  value={shipFromOverride.name}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, name: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="Street 1">
                <input
                  value={shipFromOverride.street1}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, street1: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="Street 2">
                <input
                  value={shipFromOverride.street2}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, street2: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="City">
                <input
                  value={shipFromOverride.city}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, city: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="State">
                <input
                  value={shipFromOverride.state}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, state: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="Postal Code">
                <input
                  value={shipFromOverride.postalCode}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, postalCode: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="Country">
                <input
                  value={shipFromOverride.country}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, country: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
              <Field label="Phone">
                <input
                  value={shipFromOverride.phone}
                  onChange={(e) => setShipFromOverride((prev) => ({ ...prev, phone: e.target.value }))}
                  style={S.input}
                  disabled={saving}
                />
              </Field>
            </div>
          ) : null}
        </div>

        {/* Variants Card */}
        <div style={S.card}>
          <div style={S.sectionTitle}>🧩 Variants (Color / Size / ...)</div>

          <div style={S.row3}>
            <Field label="Option type">
              <select
                value={optionTypeInput}
                onChange={(e) => {
                  setOptionTypeInput(e.target.value);
                  setOptionNameInput("");
                  setErr("");
                }}
                style={S.select}
                disabled={saving}
              >
                <option value="">Select type</option>
                {OPTION_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </Field>

            <Field label="Option name">
              <input
                ref={optionNameRef}
                value={optionNameInput}
                onChange={(e) => {
                  setOptionNameInput(e.target.value);
                  setErr("");
                }}
                onKeyDown={onOptionInputKeyDown}
                style={S.input}
                placeholder="Optional (leave empty to use selected type)"
                disabled={saving}
              />
              <div style={S.help}>Leave empty to use selected Option type, or type a custom name.</div>
            </Field>

            <Field label="Values (comma separated)">
              <input
                value={optionValuesInput}
                onChange={(e) => {
                  setOptionValuesInput(e.target.value);
                  setErr("");
                }}
                onKeyDown={onOptionInputKeyDown}
                style={S.input}
                placeholder="S,M,L,XL"
                disabled={saving}
              />
            </Field>
          </div>

          <div style={{ display: "flex", alignItems: "end", marginBottom: 14 }}>
            <button type="button" style={S.btnGhost} onClick={addOption} disabled={saving}>
              + Add option
            </button>
          </div>

          {options.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
              {options.map((opt) => (
                <div key={opt.name} style={S.variantOptionPill}>
                  <span style={{ fontWeight: 800 }}>{opt.name}:</span>
                  <span>{(opt.values || []).join(", ")}</span>
                  <button type="button" onClick={() => removeOption(opt.name)} style={S.variantPillX}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {options.length > 0 && !colorOption ? (
            <div style={S.warnBox}>Add option name <b>Color</b> with values like Red, Green, Blue.</div>
          ) : null}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <button type="button" style={S.btnPrimary} onClick={generateVariants} disabled={saving || options.length === 0}>
              Generate Variants
            </button>

            <input
              value={variantQuery}
              onChange={(e) => setVariantQuery(e.target.value)}
              style={{ ...S.input, maxWidth: 220 }}
              placeholder="Search variant or SKU"
              disabled={saving || variants.length === 0}
            />

            <input
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              style={{ ...S.input, maxWidth: 150 }}
              placeholder="Bulk price"
              disabled={saving}
            />
            <button type="button" style={S.btnGhost} onClick={applyBulkPrice} disabled={saving || variants.length === 0}>
              Apply price to all
            </button>

            <input
              value={bulkStock}
              onChange={(e) => setBulkStock(e.target.value)}
              style={{ ...S.input, maxWidth: 150 }}
              placeholder="Bulk stock"
              disabled={saving}
            />
            <button type="button" style={S.btnGhost} onClick={applyBulkStock} disabled={saving || variants.length === 0}>
              Apply stock to all
            </button>
          </div>

          {variantWarning ? <div style={S.warnBox}>{variantWarning}</div> : null}

          {variants.length > 0 ? (
            <div style={{ marginBottom: 10, fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>
              {colorOption
                ? `Showing ${colorGroups.length} color box(es) for ${variants.length} variant(s)`
                : `Showing ${variantRows.length} variant(s)`}
            </div>
          ) : null}

          {variants.length > 0 && colorOption && colorGroups.length > 0 ? (
            <div style={S.colorCardsGrid}>
              {colorGroups.map((group) => {
                const colorKey = `${normLower(colorOption.name)}:${normLower(group.color)}`;
                return (
                  <div key={group.color} style={S.colorCard}>
                    <div style={S.colorCardHeader}>
                      <div style={{ fontWeight: 900 }}>{group.color}</div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 700 }}>
                        {group.rows.length} size(s)
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                      <label style={{ ...S.btnGhost, padding: "6px 10px", fontSize: 12 }}>
                        {uploadingColorKey === colorKey ? "Uploading..." : `Upload images for ${group.color} (max 6)`}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleUploadForColor(colorOption.name, group.color, e)}
                          style={{ display: "none" }}
                          disabled={saving || uploadingColorKey === colorKey}
                        />
                      </label>

                      <span style={{ fontSize: 12, color: "var(--text-secondary)", fontWeight: 700 }}>
                        {group.images.length} image(s)
                      </span>

                      <input
                        value={colorStockDraft[normLower(group.color)] ?? ""}
                        onChange={(e) =>
                          setColorStockDraft((prev) => ({ ...prev, [normLower(group.color)]: e.target.value }))
                        }
                        style={{ ...S.variantInput, maxWidth: 120 }}
                        placeholder="Stock"
                        disabled={saving}
                      />
                      <button
                        type="button"
                        style={{ ...S.btnGhost, padding: "6px 10px", fontSize: 12 }}
                        onClick={() => applyStockToColor(colorOption.name, group.color)}
                        disabled={saving}
                      >
                        Apply stock to this box
                      </button>
                    </div>

                    {group.images.length > 0 ? (
                      <div style={{ ...S.variantImageGrid, marginBottom: 10 }}>
                        {group.images.map((u) => (
                          <div key={`${group.color}-${u}`} style={S.variantImageCard}>
                            <img src={toAbsUrl(u)} alt={group.color} style={S.variantImageThumb} />
                            <button
                              type="button"
                              style={S.variantImageRemove}
                              onClick={() => removeColorImage(colorOption.name, group.color, u)}
                              disabled={saving}
                              title="Remove image"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div style={S.sizeRowsWrap}>
                      <div style={S.sizeRowHeader}>
                        <span>Size</span>
                        <span>Stock *</span>
                        <span>SKU</span>
                        <span>Price override</span>
                      </div>

                      {group.rows.map(({ size, variant }) => (
                        <div key={`${group.color}-${size || "default"}`} style={S.sizeRow}>
                          <span style={S.sizeCellLabel}>{size || "-"}</span>
                          <input
                            value={variant?.stock ?? 0}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              updateVariantByColorAndSize(
                                colorOption.name,
                                group.color,
                                sizeOption?.name || "",
                                size,
                                { stock: Number.isFinite(val) ? Math.max(0, Math.floor(val)) : 0 }
                              );
                            }}
                            style={S.variantInput}
                            disabled={saving || !variant}
                          />
                          <input
                            value={variant?.sku || ""}
                            onChange={(e) =>
                              updateVariantByColorAndSize(
                                colorOption.name,
                                group.color,
                                sizeOption?.name || "",
                                size,
                                { sku: e.target.value }
                              )
                            }
                            style={S.variantInput}
                            disabled={saving || !variant}
                          />
                          <input
                            value={variant?.priceOverride ?? ""}
                            onChange={(e) => {
                              const val = e.target.value === "" ? "" : Number(e.target.value);
                              updateVariantByColorAndSize(
                                colorOption.name,
                                group.color,
                                sizeOption?.name || "",
                                size,
                                {
                                  priceOverride: val === "" || Number.isFinite(val) ? val : "",
                                }
                              );
                            }}
                            style={S.variantInput}
                            placeholder={`default ${localPrice || ""}`}
                            disabled={saving || !variant}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : variants.length > 0 && colorOption && colorGroups.length === 0 ? (
            <div style={S.help}>No color boxes match your search.</div>
          ) : variants.length > 0 ? (
            <div style={S.variantTableWrap}>
              <table style={S.variantTable}>
                <thead>
                  <tr>
                    {options.map((opt) => (
                      <th key={opt.name} style={S.variantTh}>{opt.name}</th>
                    ))}
                    <th style={S.variantTh}>Stock *</th>
                    <th style={S.variantTh}>SKU</th>
                    <th style={S.variantTh}>Price override</th>
                    <th style={S.variantTh}>Photos (max 6)</th>
                  </tr>
                </thead>
                <tbody>
                  {variantRows.map(({ index, variant }) => (
                    <tr key={variantKey(variant?.optionValues)}>
                      {options.map((opt) => (
                        <td key={`${variantKey(variant?.optionValues)}-${opt.name}`} style={S.variantTd}>
                          {optionValueOf(variant, opt.name) || "-"}
                        </td>
                      ))}
                      <td style={S.variantTd}>
                        <input
                          value={variant?.stock ?? 0}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setVariants((prev) =>
                              prev.map((v, i) =>
                                i === index
                                  ? { ...v, stock: Number.isFinite(val) ? Math.max(0, Math.floor(val)) : 0 }
                                  : v
                              )
                            );
                          }}
                          style={S.variantInput}
                          disabled={saving}
                        />
                      </td>
                      <td style={S.variantTd}>
                        <input
                          value={variant?.sku || ""}
                          onChange={(e) =>
                            setVariants((prev) =>
                              prev.map((v, i) => (i === index ? { ...v, sku: e.target.value } : v))
                            )
                          }
                          style={S.variantInput}
                          disabled={saving}
                        />
                      </td>
                      <td style={S.variantTd}>
                        <input
                          value={variant?.priceOverride ?? ""}
                          onChange={(e) => {
                            const val = e.target.value === "" ? "" : Number(e.target.value);
                            setVariants((prev) =>
                              prev.map((v, i) =>
                                i === index
                                  ? {
                                      ...v,
                                      priceOverride: val === "" || Number.isFinite(val) ? val : "",
                                    }
                                  : v
                              )
                            );
                          }}
                          style={S.variantInput}
                          placeholder={`default ${localPrice || ""}`}
                          disabled={saving}
                        />
                      </td>
                      <td style={S.variantTd}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                          {(Array.isArray(variant?.images) ? variant.images : []).map((url) => (
                            <div key={url} style={{ position: "relative", width: 36, height: 36 }}>
                              <img src={toAbsUrl(url)} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)" }} />
                              <button
                                type="button"
                                onClick={() => setVariants((prev) => prev.map((v, i) => i === index ? { ...v, images: (v.images || []).filter((u) => u !== url) } : v))}
                                style={{ position: "absolute", top: -4, right: -4, background: "#ef4444", color: "#fff", border: "none", borderRadius: "50%", width: 16, height: 16, fontSize: 9, cursor: "pointer", lineHeight: "16px", padding: 0 }}
                                title="Remove"
                              >✕</button>
                            </div>
                          ))}
                          {(Array.isArray(variant?.images) ? variant.images : []).length < 6 && (
                            <label style={{ ...S.btnGhost, padding: "4px 8px", fontSize: 11, cursor: "pointer" }}>
                              {uploadingColorKey === `row:${index}` ? "..." : "+ Photo"}
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                disabled={saving || uploadingColorKey === `row:${index}`}
                                onChange={async (e) => {
                                  const file = e?.target?.files?.[0];
                                  if (!file) return;
                                  setUploadingColorKey(`row:${index}`);
                                  try {
                                    const url = await uploadVariantImage(file);
                                    setVariants((prev) => prev.map((v, i) => i === index ? { ...v, images: [...(v.images || []), url].slice(0, 6) } : v));
                                  } catch (err) {
                                    setErr(err?.message || "Upload failed");
                                  } finally {
                                    setUploadingColorKey("");
                                    if (e?.target) e.target.value = "";
                                  }
                                }}
                              />
                            </label>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {variantRows.length === 0 ? <div style={{ ...S.help, marginTop: 8 }}>No variants match your search.</div> : null}
            </div>
          ) : (
            <div style={S.help}>No variants yet. Add options then click Generate Variants.</div>
          )}
        </div>

        {/* AI Targeting Card */}
        <div style={S.card}>
          <div style={S.sectionTitle}>🎯 AI Mall Targeting</div>

          <div style={S.row3}>
            <Field label="Gender">
              <select value={genderMode} onChange={(e) => setGenderMode(e.target.value)} style={S.select} disabled={saving}>
                <option value="any">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <div style={S.help}>“Any” = empty genders[] (shows to everyone)</div>
            </Field>

            <Field label="Min Age * (required)">
              <input
                value={minAge}
                onChange={(e) => setMinAge(e.target.value)}
                style={S.input}
                inputMode="numeric"
                disabled={saving}
                placeholder="e.g. 10"
                required
              />
            </Field>

            <Field label="Max Age * (required)">
              <input
                value={maxAge}
                onChange={(e) => setMaxAge(e.target.value)}
                style={S.input}
                inputMode="numeric"
                disabled={saving}
                placeholder="e.g. 60"
                required
              />
            </Field>
          </div>

          <div style={S.row2}>
            <Field label="Favorite Sports">
              <MultiDropdown
                options={SPORT_OPTIONS}
                value={sports}
                setValue={setSports}
                placeholder="Select sports…"
                disabled={saving}
              />
              <div style={S.help}>Backend field: targeting.sports[]</div>
            </Field>

            <Field label="Interests">
              <MultiDropdown
                options={INTEREST_OPTIONS}
                value={interests}
                setValue={setInterests}
                placeholder="Select interests…"
                disabled={saving}
              />
              <div style={S.help}>Backend field: targeting.interests[]</div>
            </Field>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
          <button type="button" style={S.btnGhost} onClick={() => nav("/shop/products")} disabled={saving}>
            Cancel
          </button>
          <button type="submit" style={S.btnPrimary} disabled={saving}>
            {saving ? "Saving..." : "Add Product"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* =========================
   Static options
   ========================= */
const SPORT_OPTIONS = [
  "soccer", "basketball", "football", "american football", "baseball", "softball",
  "tennis", "padel", "pickleball", "badminton", "table tennis", "squash",
  "cricket", "rugby", "field hockey", "ice hockey", "volleyball", "beach volleyball",
  "handball", "boxing", "mma", "wrestling", "judo", "karate", "taekwondo",
  "muay thai", "golf", "swimming", "running", "cycling", "triathlon", "rowing",
  "canoeing", "kayaking", "sailing", "surfing", "skateboarding", "skiing",
  "snowboarding", "gymnastics", "weightlifting", "powerlifting", "crossfit",
  "climbing", "archery", "shooting", "darts", "bowling", "billiards", "snooker",
  "chess", "formula 1", "motogp", "nascar", "e-sports", "athletics",
  "equestrian", "other",
];

const INTEREST_OPTIONS = [
  "fashion", "beauty", "skincare", "makeup", "fitness", "health", "wellness",
  "nutrition", "tech", "gadgets", "ai", "gaming", "e-sports", "sports", "cars",
  "motorcycles", "home & decor", "interior design", "furniture", "kitchen",
  "food & cooking", "baking", "coffee", "travel", "adventure", "education",
  "online courses", "books", "comics", "kids", "babies", "parenting", "pets",
  "music", "movies & tv", "photography", "videography", "luxury", "art", "design",
  "diy & crafts", "gardening", "outdoors", "camping", "hiking", "fishing",
  "jewelry", "watches", "streetwear", "vintage", "anime", "k-pop",
  "sustainability", "business", "entrepreneurship", "finance", "crypto", "science",
  "history", "culture", "budget",
];

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={S.label}>{label}</div>
      {children}
    </div>
  );
}

// ✅ Chip dropdown multi-select
function MultiDropdown({ options = [], value = [], setValue, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  const normalizedValue = useMemo(() => normLowerList(value), [value]);

  const filtered = useMemo(() => {
    const qq = String(q || "").toLowerCase().trim();
    const base = options.map((x) => String(x || "").toLowerCase().trim()).filter(Boolean);
    if (!qq) return base;
    return base.filter((x) => x.includes(qq));
  }, [options, q]);

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function toggle(v) {
    const x = String(v || "").toLowerCase().trim();
    if (!x) return;
    if (normalizedValue.includes(x)) setValue(normalizedValue.filter((a) => a !== x));
    else setValue([...normalizedValue, x]);
  }

  function removeChip(v) {
    const x = String(v || "").toLowerCase().trim();
    setValue(normalizedValue.filter((a) => a !== x));
  }

  return (
    <div ref={boxRef} style={S.ddWrap}>
      <button
        type="button"
        style={{
          ...S.ddBtn,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onClick={() => !disabled && setOpen((p) => !p)}
        disabled={disabled}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {normalizedValue.length === 0 ? (
            <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{placeholder}</span>
          ) : (
            normalizedValue.slice(0, 6).map((x) => (
              <span key={x} style={S.chip} onClick={(e) => e.stopPropagation()}>
                {x}
                <span
                  style={S.chipX}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeChip(x);
                  }}
                >
                  ✕
                </span>
              </span>
            ))
          )}
          {normalizedValue.length > 6 ? (
            <span style={{ color: "var(--text-secondary)", fontWeight: 700 }}>+{normalizedValue.length - 6}</span>
          ) : null}
        </div>
        <span style={{ color: "var(--text-secondary)", fontWeight: 700 }}>▾</span>
      </button>

      {open ? (
        <div style={S.ddPanel}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            style={S.ddSearch}
            disabled={disabled}
          />

          <div style={S.ddList}>
            {filtered.length === 0 ? (
              <div style={{ padding: 10, color: "var(--text-secondary)", fontWeight: 600 }}>No matches</div>
            ) : (
              filtered.map((opt) => {
                const on = normalizedValue.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggle(opt)}
                    style={{
                      ...S.ddItem,
                      background: on ? "rgba(37,99,235,0.12)" : "transparent",
                      borderColor: on ? "var(--accent)" : "transparent",
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{opt}</span>
                    <span style={{ color: "var(--accent)", fontWeight: 900 }}>{on ? "✓" : ""}</span>
                  </button>
                );
              })
            )}
          </div>

          {normalizedValue.length > 0 ? (
            <button type="button" style={S.ddClear} onClick={() => setValue([])}>
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const S = {
  wrap: { 
    color: "var(--text-primary)", 
    fontFamily: "Arial",
    background: "var(--bg-primary)",
    minHeight: "100vh",
    padding: "20px",
    maxWidth: 1200,
    margin: "0 auto"
  },

  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 12,
  },
  h1: { 
    fontSize: 22, 
    fontWeight: 900,
    color: "var(--text-primary)"
  },
  sub: { 
    color: "var(--text-secondary)", 
    marginTop: 4, 
    fontWeight: 600,
    fontSize: 14
  },
  metaRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 },
  metaPill: {
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    fontWeight: 700,
    fontSize: 12,
    color: "var(--text-secondary)",
  },

  card: {
    background: "var(--card-bg)",
    border: "1px solid var(--border-color)",
    borderRadius: 12,
    padding: 24,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: 16
  },

  sectionTitle: { 
    fontWeight: 900, 
    marginTop: 8,
    marginBottom: 16,
    color: "var(--text-primary)",
    fontSize: 16,
    paddingBottom: 12,
    borderBottom: "2px solid var(--border-color)"
  },

  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  row3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 },

  label: { 
    fontWeight: 700, 
    color: "var(--text-primary)", 
    fontSize: 13,
    marginBottom: 6,
    display: "block"
  },

  input: {
    padding: "12px 14px",
    borderRadius: 8,
    border: "1.5px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    outline: "none",
    fontWeight: 600,
    fontSize: 14,
    width: "100%",
    transition: "border-color 0.2s, box-shadow 0.2s"
  },
  textarea: {
    padding: "12px 14px",
    borderRadius: 8,
    border: "1.5px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    outline: "none",
    fontWeight: 600,
    minHeight: 110,
    resize: "vertical",
    fontFamily: "Arial",
    fontSize: 14,
    width: "100%",
    transition: "border-color 0.2s"
  },

  select: {
    padding: "12px 14px",
    borderRadius: 8,
    border: "1.5px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    outline: "none",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    width: "100%",
    transition: "border-color 0.2s"
  },

  help: { 
    color: "var(--text-secondary)", 
    fontWeight: 600, 
    fontSize: 12, 
    marginTop: 6 
  },

  switchRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },

  uploadBox: {
    padding: 20,
    borderRadius: 12,
    border: "2px dashed var(--border-color)",
    background: "var(--bg-tertiary)",
    display: "grid",
    gap: 16,
    marginBottom: 16
  },
  uploadBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    borderRadius: 8,
    border: "1.5px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    fontWeight: 700,
    cursor: "pointer",
    width: "fit-content",
    color: "var(--text-primary)",
    fontSize: 14
  },

  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 10,
  },
  previewCard: {
    borderRadius: 14,
    border: "1px solid var(--border-color)",
    background: "var(--card-bg)",
    overflow: "hidden",
    position: "relative",
  },
  previewImg: { width: "100%", height: 120, objectFit: "cover", display: "block" },
  previewName: { 
    padding: 8, 
    fontSize: 12, 
    fontWeight: 600, 
    color: "var(--text-secondary)" 
  },
  removeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    border: "none",
    background: "rgba(239,68,68,0.9)",
    color: "#fff",
    borderRadius: 10,
    padding: "6px 8px",
    cursor: "pointer",
    fontWeight: 900,
  },

  btnPrimary: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--btn-outline-border)",
    background: "var(--btn-primary-bg)",
    color: "var(--btn-primary-text)",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 14
  },
  btnGhost: {
    padding: "10px 18px",
    borderRadius: 10,
    border: "1px solid var(--btn-outline-border)",
    background: "var(--btn-secondary-bg)",
    color: "var(--btn-secondary-text)",
    fontWeight: 700,
    cursor: "pointer",
    fontSize: 14
  },

  errBox: {
    marginBottom: 10,
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(248, 113, 113, 0.4)",
    background: "rgba(248, 113, 113, 0.12)",
    fontWeight: 900,
    color: "var(--danger)"
  },

  warnBox: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 10,
    border: "1px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    fontWeight: 700,
    fontSize: 13,
  },

  variantOptionPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    fontSize: 12,
  },
  variantPillX: {
    border: "none",
    background: "transparent",
    color: "var(--text-secondary)",
    cursor: "pointer",
    fontWeight: 900,
  },
  colorCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: 12,
  },
  colorCard: {
    border: "1px solid var(--border-color)",
    borderRadius: 12,
    background: "var(--bg-tertiary)",
    padding: 12,
  },
  colorCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  sizeRowsWrap: {
    border: "1px solid var(--border-color)",
    borderRadius: 10,
    overflow: "hidden",
    background: "var(--card-bg)",
  },
  sizeRowHeader: {
    display: "grid",
    gridTemplateColumns: "100px 1fr 1fr 1fr",
    gap: 8,
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    fontSize: 12,
    fontWeight: 800,
    color: "var(--text-secondary)",
    background: "var(--bg-secondary)",
  },
  sizeRow: {
    display: "grid",
    gridTemplateColumns: "100px 1fr 1fr 1fr",
    gap: 8,
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    alignItems: "center",
  },
  sizeCellLabel: {
    fontWeight: 800,
    color: "var(--text-primary)",
    fontSize: 13,
  },
  variantTableWrap: {
    overflowX: "auto",
    border: "1px solid var(--border-color)",
    borderRadius: 10,
  },
  variantTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 920,
    background: "var(--card-bg)",
  },
  variantTh: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 800,
    padding: "10px 10px",
    borderBottom: "1px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    color: "var(--text-secondary)",
  },
  variantTd: {
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    fontSize: 13,
    color: "var(--text-primary)",
    verticalAlign: "middle",
  },
  variantInput: {
    width: "100%",
    minWidth: 120,
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    color: "var(--text-primary)",
    fontSize: 12,
    fontWeight: 600,
  },
  variantImageGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  variantImageCard: {
    width: 46,
    height: 46,
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    border: "1px solid var(--border-color)",
    background: "var(--bg-tertiary)",
  },
  variantImageThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  variantImageRemove: {
    position: "absolute",
    top: 0,
    right: 0,
    border: "none",
    background: "rgba(239,68,68,0.9)",
    color: "#fff",
    width: 18,
    height: 18,
    fontSize: 11,
    lineHeight: 1,
    cursor: "pointer",
    fontWeight: 900,
  },

  // dropdown styles
  ddWrap: { position: "relative" },
  ddBtn: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    padding: "12px 14px",
    borderRadius: 8,
    border: "1.5px solid var(--border-color)",
    background: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 14
  },
  ddPanel: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    right: 0,
    zIndex: 50,
    borderRadius: 14,
    border: "1px solid var(--border-color)",
    background: "var(--card-bg)",
    overflow: "hidden",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)"
  },
  ddSearch: {
    width: "100%",
    padding: "10px 12px",
    border: "none",
    outline: "none",
    background: "var(--bg-tertiary)",
    color: "var(--text-primary)",
    fontWeight: 600,
    fontSize: 14
  },
  ddList: { maxHeight: 220, overflow: "auto", padding: 6 },
  ddItem: {
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid transparent",
    background: "transparent",
    color: "var(--text-primary)",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 600,
    marginBottom: 6,
    fontSize: 14
  },
  ddClear: {
    width: "100%",
    border: "none",
    background: "rgba(248, 113, 113, 0.16)",
    color: "var(--danger)",
    padding: "10px 12px",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 14
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid var(--border-color)",
    background: "var(--bg-secondary)",
    fontWeight: 700,
    fontSize: 12,
    color: "var(--text-primary)"
  },
  chipX: {
    display: "inline-flex",
    width: 18,
    height: 18,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg-tertiary)",
    cursor: "pointer",
    fontWeight: 900,
    color: "var(--text-secondary)"
  },
};
